import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';

declare global {
  interface Window {
    // Matches the optional declaration in src/utils/analytics.ts so the
    // interfaces merge cleanly. The inline gtag in index.html attaches it
    // before this component ever runs.
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const STORAGE_KEY = 'cookie_consent';
type ConsentValue = 'accepted' | 'rejected';

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);

  // Read localStorage in an effect to avoid any SSR / hydration mismatch.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== 'accepted' && stored !== 'rejected') {
        setVisible(true);
      }
    } catch {
      // localStorage unavailable (private mode, restricted) — show the banner
      // so the user can still make a choice for this session.
      setVisible(true);
    }
  }, []);

  const persist = (value: ConsentValue) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Storage may be unavailable; the banner will reappear next visit.
    }
  };

  const handleAccept = () => {
    persist('accepted');
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      });
    }
    setVisible(false);
  };

  const handleReject = () => {
    persist('rejected');
    // Leave consent denied — no gtag update needed.
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
        >
          <div className="pointer-events-auto mx-auto max-w-3xl bg-white border border-slate-200 rounded-2xl shadow-lg p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="flex items-start gap-3 flex-1">
                <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                  <Cookie className="h-5 w-5" aria-hidden="true" />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We use cookies to analyze site usage and improve your experience. See our{' '}
                  <Link
                    to="/privacy"
                    className="text-indigo-600 hover:text-indigo-700 font-medium underline underline-offset-2"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:shrink-0">
                <button
                  type="button"
                  onClick={handleReject}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-primary"
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
